// we disabled sort key because CSS depends on key order here
/* eslint-disable sort-keys */
import { SxProps } from '@mui/system';
import { ButtonStyleProps as TimelineItemStyleProps, Variant } from './types';
import { accentColors } from '@styles/theme';

// ********** CONSTS **********

const ITEM_DOT_SIZE = 13;
const TIMELINE_AXIS_WIDTH = 1;
const AXIS_MIN_DISTANCE = ITEM_DOT_SIZE + TIMELINE_AXIS_WIDTH;

const CLASS_PREFIX = 'timeline-item';
const BUTTON_CLASS = `${CLASS_PREFIX}-button`;
const DOT_CLASS = `${CLASS_PREFIX}-dot`;
const WRAPPER_CLASS = `${CLASS_PREFIX}-wrapper`;

export { BUTTON_CLASS, DOT_CLASS, TIMELINE_AXIS_WIDTH, WRAPPER_CLASS };

// ********** STYLES **********

const getVariantColor = (variant?: Variant): string | null => {
    switch (variant) {
        case 'compareBefore': {
            return accentColors[5];
        }
        case 'compareAfter': {
            return 'info.main';
        }
        default:
            return '';
    }
};

const getInternalDotSize = (isActive: boolean, isSelected: boolean): number => {
    if (isSelected) return ITEM_DOT_SIZE;

    return isActive ? ITEM_DOT_SIZE / 2 : 0;
};

const getVariantStyles = ({
    disabled,
    isActive,
    level,
    variant,
}: TimelineItemStyleProps): SxProps => {
    const disabledWithoutVariant = disabled && !variant;
    const variantColor = getVariantColor(variant);
    const variantSx = variantColor
        ? {
              [`.${DOT_CLASS}::after`]: { bgcolor: variantColor },
              borderColor: variantColor,
          }
        : {};

    return {
        '&:hover': {
            [`.${BUTTON_CLASS}:not([aria-disabled="true"])`]: {
                bgcolor: 'primary.light',
            },
            borderColor: 'primary.dark',
        },
        [`.${BUTTON_CLASS}`]: {
            p: 1,
            ...(disabledWithoutVariant ? { bgcolor: 'grey.600' } : {}),
            ...(isActive && { bgcolor: 'grey.100' }),
        },
        '&&': {
            ...(disabledWithoutVariant ? { borderColor: 'grey.400' } : {}),
            ...(isActive && { pointerEvents: 'none' }),
            ...variantSx,
        },
        borderColor: 'grey.100',
        zIndex: 100 - level,
    };
};

const containerSx = (left: number, bottom: number, itemProps: TimelineItemStyleProps): SxProps => ({
    [`.${WRAPPER_CLASS}`]: {
        border: 1,
        borderColor: 'inherit',
        borderRadius: 0.5,
        bottom: bottom + ITEM_DOT_SIZE / 2 + AXIS_MIN_DISTANCE - 1,
        position: 'absolute',
        transform: 'translateX(-50%)',
        bgcolor: 'background.default',
    },
    left,
    position: 'absolute',
    top: '100%',
    transform: 'translateY(-50%)',
    ...getVariantStyles(itemProps),
});

const dotSx = (
    { isActive, isSelected }: { isActive: boolean; isSelected: boolean },
    bottomShift: number,
): SxProps => {
    const internalDotSize = getInternalDotSize(isActive, isSelected);
    const borderWidth = 1;
    const pseudoCommon: SxProps = {
        content: "''",
        display: 'block',
        position: 'absolute',
    };

    return {
        ...(internalDotSize && {
            '::after': {
                ...pseudoCommon,
                bgcolor: 'grey.100',
                borderRadius: '50%',
                height: internalDotSize,
                left: '50%',
                top: '50%',
                transform: 'translate3D(-50%,-50%,0)',
                width: internalDotSize,
            },
        }),
        '::before': {
            ...pseudoCommon,
            borderLeft: borderWidth,
            borderColor: 'inherit',
            bottom: ITEM_DOT_SIZE - borderWidth,
            height: bottomShift + AXIS_MIN_DISTANCE,
            left: `calc(50% - ${borderWidth / 2}px)`,
            width: 0,
        },
        bgcolor: 'background.default',
        border: borderWidth,
        borderColor: 'inherit',
        borderRadius: '50%',
        bottom: -(ITEM_DOT_SIZE + TIMELINE_AXIS_WIDTH) / 2,
        height: ITEM_DOT_SIZE,
        position: 'absolute',
        transform: 'translateX(-50%)',
        width: ITEM_DOT_SIZE,
    };
};

const sharedTextSx: SxProps = {
    fontSize: 12,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    width: '100%',
};

const textSx = (active: boolean): Record<'contentSx' | 'titleSx', SxProps> => ({
    contentSx: {
        ...sharedTextSx,
        color: active ? 'grey.800' : 'grey.100',
        fontWeight: 'medium',
        lineHeight: 2,
    },
    titleSx: {
        ...sharedTextSx,
        color: active ? 'grey.500' : 'grey.300',
    },
});

export { containerSx, dotSx, textSx };
